import React, { useState, useEffect } from "react";
import { Trophy, Star, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface PostWorkoutFeedbackProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rpe: number, comments: string) => void;
  workoutTitle?: string;
}

const rpeLabels = [
  "Very light",
  "Light",
  "Light",
  "Moderate (somewhat hard)",
  "Moderate (somewhat hard)",
  "High (vigorous)",
  "High (vigorous)",
  "Very hard",
  "Very hard",
  "Maximum effort (highest possible)",
];

export function PostWorkoutFeedback({ 
  isOpen, 
  onClose, 
  onSubmit, 
  workoutTitle = "Your Workout"
}: PostWorkoutFeedbackProps) {
  const [rpe, setRpe] = useState([6]); // Default to mid-scale
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form state when dialog closes so the next session starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setRpe([6]);
      setComments("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Update the existing CompletedWorkout with feedback data
      await onSubmit(rpe[0], comments);
      // onSubmit will handle navigation and cleanup, so we don't need to call onClose here
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setIsSubmitting(false);
      // Show error to user (you could add a toast notification here)
      // On error, still close the popup and redirect
      onClose();
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Just close - onClose will handle navigation and cleanup
      onClose();
    }
  };

  const handleSkip = () => {
    if (!isSubmitting) {
      // Skip just closes and redirects - workout is already saved with default values
      // No need to call onSubmit since CompletedWorkout was already saved when popup appeared
      onClose();
    }
  };

  const currentRpe = rpe[0];
  const currentLabel = rpeLabels[currentRpe - 1];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl p-0 overflow-hidden bg-dark-card text-white border border-primary/20 max-h-[90vh] flex flex-col [&>button]:text-black [&>button]:hover:text-black/80 [&>button]:data-[state=open]:text-black/80 [&>button]:focus:ring-black/30">
        {/* Header with celebration design */}
        <div className="gradient-primary px-4 py-3 sm:px-6 sm:py-5 text-black relative overflow-hidden">
          <div className="absolute -top-4 -right-4 opacity-20">
            <Trophy className="h-20 w-20 sm:h-24 sm:w-24" />
          </div>
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-lg sm:text-xl text-center text-black flex items-center justify-center gap-2 leading-tight">
              <Trophy className="h-5 w-5 shrink-0" />
              Workout complete
            </DialogTitle>
          </DialogHeader>
          <div className="text-center mt-2 text-black/80">
            <p className="text-sm sm:text-base font-semibold leading-snug">
              Great job finishing "{workoutTitle}"!
            </p>
            <p className="text-xs sm:text-sm mt-1 text-black/70">
              Quick feedback helps us tailor what’s next
            </p>
          </div>
        </div>
        
        <div className="p-4 sm:p-6 space-y-6 overflow-y-auto min-h-0">
          {/* Difficulty Rating */}
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="font-semibold text-white mb-1">How hard was this workout?</h3>
              <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mb-2">
                <span className="text-lg font-semibold text-white tabular-nums">{currentRpe} / 10</span>
                <span className="text-sm font-medium text-gray-light text-center">{currentLabel}</span>
              </div>
            </div>
            
            <div className="px-4">
              <Slider
                value={rpe}
                onValueChange={setRpe}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-light/80 mt-2">
                {Array.from({ length: 10 }).map((_, index) => (
                  <span key={index} className="text-center flex-1">
                    {index + 1}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-white">Additional comments (optional)</h3>
            </div>
            <Textarea
              placeholder="How did you feel? Any notes for next time? What went well?"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="min-h-[100px] resize-none bg-dark-slate text-white placeholder:text-gray-light/60 border-primary/20 focus-visible:ring-primary/40 focus-visible:ring-offset-0"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-0 sm:space-x-3">
            <Button
              variant="outline"
              onClick={handleSkip}
              disabled={isSubmitting}
              className="flex-1"
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Star className="h-4 w-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 